import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const SupplierHeader = ({ supplier, onChatClick, onFavoriteToggle, isFavorite }) => {
  return (
    <div className="bg-card rounded-xl shadow-warm-md overflow-hidden">
      <div className="relative h-48 md:h-64 lg:h-80 overflow-hidden">
        <Image
          src={supplier?.coverImage}
          alt={supplier?.coverImageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <button
          onClick={onFavoriteToggle}
          className="absolute top-4 right-4 w-12 h-12 bg-card/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-card transition-smooth shadow-warm"
        >
          <Icon
            name={isFavorite ? "Heart" : "Heart"}
            size={24}
            color={isFavorite ? "var(--color-accent)" : "var(--color-foreground)"}
            className={isFavorite ? "fill-current" : ""}
          />
        </button>
      </div>
      <div className="p-6 md:p-8 lg:p-10">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <div className="flex-shrink-0">
            <div className="relative w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 rounded-xl overflow-hidden shadow-warm-lg">
              <Image
                src={supplier?.profileImage}
                alt={supplier?.profileImageAlt}
                className="w-full h-full object-cover"
              />
              {supplier?.isVerified && (
                <div className="absolute bottom-2 right-2 w-8 h-8 md:w-10 md:h-10 bg-success rounded-full flex items-center justify-center shadow-warm">
                  <Icon name="BadgeCheck" size={20} color="var(--color-success-foreground)" />
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-2">
                  {supplier?.name}
                </h1>
                <p className="text-base md:text-lg text-muted-foreground mb-3">
                  {supplier?.tagline}
                </p>
                <div className="flex flex-wrap items-center gap-3 md:gap-4">
                  <div className="flex items-center gap-1.5">
                    <Icon name="Star" size={20} color="var(--color-warning)" className="fill-current" />
                    <span className="font-semibold text-lg">{supplier?.rating}</span>
                    <span className="text-muted-foreground">({supplier?.reviewCount} reviews)</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon name="MapPin" size={18} />
                    <span className="text-sm md:text-base">{supplier?.location}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon name="Clock" size={18} />
                    <span className="text-sm md:text-base">{supplier?.responseTime}</span>
                  </div>
                </div>
              </div>

              <div className="flex sm:flex-col gap-3">
                <Button
                  variant="default"
                  size="lg"
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={onChatClick}
                  className="flex-1 sm:flex-none"
                >
                  Chat Now
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Share2"
                  iconPosition="left"
                  className="flex-1 sm:flex-none"
                >
                  Share
                </Button>
                {/* If this is the owner viewing their own kitchen, show Edit Profile */}
                {supplier?.isOwner && (
                  <Button
                    variant="secondary"
                    size="lg"
                    iconName="Edit"
                    iconPosition="left"
                    onClick={supplier?.onEditClick}
                    className="flex-1 sm:flex-none"
                  >
                    Edit Profile
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {supplier?.certifications?.map((cert, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-3 py-1.5 bg-success/10 text-success rounded-lg"
                >
                  <Icon name="ShieldCheck" size={16} />
                  <span className="text-sm font-medium">{cert}</span>
                </div>
              ))}
              {supplier?.specialties?.map((specialty, index) => (
                <div
                  key={index}
                  className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-medium"
                >
                  {specialty}
                </div>
              ))}
            </div>

            <p className="text-base md:text-lg leading-relaxed text-foreground">
              {supplier?.bio}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary mb-1">
                  {supplier?.stats?.totalOrders}+
                </div>
                <div className="text-sm text-muted-foreground">Orders Completed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary mb-1">
                  {supplier?.stats?.repeatCustomers}%
                </div>
                <div className="text-sm text-muted-foreground">Repeat Customers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary mb-1">
                  {supplier?.stats?.yearsExperience}+
                </div>
                <div className="text-sm text-muted-foreground">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary mb-1">
                  {supplier?.stats?.avgDeliveryTime}
                </div>
                <div className="text-sm text-muted-foreground">Avg Delivery</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierHeader;